import React, { useCallback, useMemo } from "react";
import * as Scrivito from "scrivito";
import { currentPage } from "scrivito";
import cx from "classnames";
import { Offcanvas, OffcanvasBody, OffcanvasHeader } from "reactstrap";
import { getHomepage, getPageLink } from "utils";

import { useUser, useIsAuthenticated } from "stores/user";
import { useProject } from "stores/project/project/projectStore";
import { lastSelectedUserLangLSKey } from "variables/localStorageItemsNames";
import { tenants } from "config/tenants";
import { useTranslate } from "hooks";
import type { TenantKey } from "interfaces/shared.types";
import { LogoutButton } from "../LogoutButton";
import { useLanguage, useSetLanguage } from "stores/globalStore";

export const NavRightSidebar = Scrivito.connect(
  ({ onToggleSideBar, isOpen }) => {
    const translate = useTranslate();
    const isAuthenticated = useIsAuthenticated();

    const user = useUser();
    const project = useProject();

    const selectedLang = useLanguage();
    const setLang = useSetLanguage();
    const supportedLanguages = useMemo(
      () => tenants[(user?.tenant as TenantKey) || "SC"]?.language,
      [user],
    );

    const homepage = getHomepage();

    const handleLinkClicked = useCallback(
      (
        e: React.MouseEvent,
        externalLink: string | null,
        objId: string | null,
        objClass: string,
      ) => {
        if (objClass === "Homepage" && objId) {
          e.preventDefault();
          const newWindow = window.open(objId, "_blank");
          if (newWindow) {
            newWindow.focus();
          }
        }
        if (objClass === "Redirect" && externalLink) {
          e.preventDefault();
          const newWindow = window.open(externalLink, "_blank");
          if (newWindow) {
            newWindow.focus();
          }
        }
        onToggleSideBar();
      },
      [onToggleSideBar],
    );

    const configuratorNavLinks =
      (homepage?.get("configuratorNavigationServiceLinks") as Scrivito.Obj[]) ||
      [];

    const handleLanguageChange = useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        const lang = e.currentTarget.dataset.lang;
        if (lang) {
          localStorage.setItem(lastSelectedUserLangLSKey, lang);
          setLang(lang);
          Scrivito.navigateTo(currentPage, Scrivito.currentPageParams());
        }
      },
      [setLang],
    );

    if (!homepage) {
      return;
    }

    return (
      <Offcanvas
        direction="end"
        toggle={onToggleSideBar}
        isOpen={isOpen}
        data-testid="right-sidebar"
      >
        <OffcanvasHeader toggle={onToggleSideBar} tag="div">
          <div className="d-flex">
            {supportedLanguages?.map((lang, key) => (
              <button
                data-testid={`NavRightSidebar_lang-${lang.key}-link`}
                onClick={handleLanguageChange}
                data-lang={lang.value}
                className={cx(
                  "me-3 p-0 border-0 bg-transparent text-decoration-none text-uppercase text-gray-700 bold-hover font-md cursor-pointer",
                  {
                    "fw-bold": selectedLang === lang.value,
                  },
                )}
                key={key}
              >
                {lang.key}
              </button>
            ))}
          </div>
        </OffcanvasHeader>
        <OffcanvasBody className="pt-6 d-flex flex-column justify-content-between">
          <div>
            {isAuthenticated && (
              <>
                <div className="d-flex flex-column mb-7">
                  <h3 className="mb-1 text-uppercase">
                    {translate("NAV_RIGHT_SIDEBAR_NAV_MENU")}
                  </h3>
                  <h4 className="mb-0">
                    {translate("NAV_RIGHT_SIDEBAR_NAV_SUBTITLE")}
                  </h4>
                </div>
                <nav className={cx("w-100")}>
                  {configuratorNavLinks.map((obj, key) => {
                    // TODO: dirty fix for ISH, pls provide a clean way (open links to
                    // Homepage/Redirections in the new tab in cleaner way)
                    const targetLink = getPageLink(obj);
                    const objClass = obj.objClass();
                    const isOnSupportPage =
                      Scrivito.currentPage()?.objClass() ===
                      "ConfiguratorSupport";
                    const objId = obj.id();
                    const externalLink = (
                      obj.get("link") as Scrivito.Link
                    )?.url();
                    const linkParams =
                      (objClass === "ConfiguratorSupport" ||
                        objClass === "ProjectList") &&
                      project?.id
                        ? { projectId: project?.id }
                        : undefined;
                    return (
                      <div key={key} className={cx("w-100 border-bottom")}>
                        <Scrivito.LinkTag
                          data-testid={`NavRightSidebar_nav-${
                            obj.get("navigationTitle") || obj.get("title")
                          }-link`}
                          to={targetLink as Scrivito.Link | Scrivito.Obj}
                          params={linkParams}
                          onClick={(e) => {
                            if (
                              objClass === "ConfiguratorSupport" &&
                              isOnSupportPage
                            ) {
                              e.preventDefault();
                              onToggleSideBar();
                              return;
                            }

                            handleLinkClicked(e, externalLink, objId, objClass);
                          }}
                          className={cx(
                            "text-decoration-none nav-link py-4 px-0 m-0",
                            {
                              "pe-none": !isAuthenticated,
                            },
                          )}
                        >
                          <div
                            className={cx(
                              "h5 mb-0 text-uppercase main-menu-link-hover",
                              {
                                "text-gray-400": !isAuthenticated,
                              },
                            )}
                          >
                            {
                              objClass === "Homepage" &&
                                translate("TROX_HOMEPAGE") // TODO: when we could publish a change for homepage title, we could get
                              // rid of this condition (now we cannot do that - it would affect Ruby-app
                              // data)
                            }
                            {objClass === "ProjectList" &&
                              translate("MY_PROJECTS")}
                            {objClass === "ContentPage" && translate("WELCOME")}
                            {objClass === "Redirect" &&
                              translate("MY_CONTACTS")}
                            {objClass === "ConfiguratorSupport" &&
                              translate("CONFIGURATOR_SUPPORT")}
                          </div>
                        </Scrivito.LinkTag>
                      </div>
                    );
                  })}
                </nav>
              </>
            )}
          </div>
          <LogoutButton />
        </OffcanvasBody>
      </Offcanvas>
    );
  },
);
